import React from 'react';
import { FaPlane, FaPassport, FaCalendarAlt, FaMapMarkedAlt, FaUserFriends, FaShieldAlt, FaClock, FaGlobe } from 'react-icons/fa';
import '../Styles/Visit.css';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect } from 'react';
import Reveal, { RevealGroup, RevealItem } from './Reveal';
import MagneticButton from './MagneticButton';

export default function Visit() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const visitImages = [
    "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=700&q=70",
    "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=700&q=70",
    "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=700&q=70",
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=700&q=70",
    "https://images.unsplash.com/photo-1522199755839-a2bacb67c546?w=700&q=70"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % visitImages.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [visitImages.length]);

  return (
    <div className="visit-page">
      <Helmet>
        <title>Tourist & Visitor Visa Services | USA, Canada, UK, Schengen</title>
        <meta name="description" content="Fast and hassle-free visitor visa processing for USA B1/B2, Canada TRV, UK standard visitor, and Schengen visas. 95% success rate. Start your journey!" />
        <link rel="canonical" href="https://veabroad.com/visit" />
        <meta property="og:title" content="Tourist & Visitor Visa Services | USA, Canada, UK, Schengen" />
        <meta property="og:description" content="Fast and hassle-free visitor visa processing for USA B1/B2, Canada TRV, UK standard visitor, and Schengen visas. 95% success rate. Start your journey!" />
        <meta property="og:url" content="https://veabroad.com/visit" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tourist & Visitor Visa Services | USA, Canada, UK, Schengen" />
        <meta name="twitter:description" content="Fast and hassle-free visitor visa processing for USA B1/B2, Canada TRV, UK standard visitor, and Schengen visas. 95% success rate. Start your journey!" />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Visitor & Tourist Visa Assistance",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            },
            "areaServed": "IN",
            "description": "Complete documentation, flight itinerary assistance, and submission support for visitor and business visas."
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="visit-hero">
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-teal" style={{ top: "-10%", left: "60%", width: "380px", height: "380px" }}></div>
          <div className="va-blob va-blob-mint" style={{ top: "50%", left: "5%", width: "300px", height: "300px" }}></div>
        </div>
        <div className="container position-relative">
          <div className="row align-items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="col-lg-6"
            >
              <h1 className="hero-title text-white">Explore the <span className="va-shine-text">World</span> with Confidence</h1>
              <p className="hero-subtitle text-white">
                Whether it's business, tourism, or visiting family, we make your travel dreams a reality with expert visa assistance and travel planning.
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-number va-pop">50+</span>
                  <span className="stat-label visit-stat-label">Countries</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number va-pop">95%</span>
                  <span className="stat-label visit-stat-label">Success Rate</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number va-pop">1000+</span>
                  <span className="stat-label visit-stat-label">Happy Travelers</span>
                </div>
              </div>
              <MagneticButton
                as={Link}
                to="/consultationForm"
                className="premium-btn pulse-animation va-shimmer-btn d-block text-center mt-3 text-white"
                style={{ maxWidth: '260px', margin: '1rem auto 0' }}
              >
                Start Your Journey
              </MagneticButton>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="col-lg-6"
            >
              <div className="visit-hero-slideshow position-relative">
                {/* Desktop Slideshow */}
                <div className="hero-slideshow-container d-none d-lg-block" style={{ height: "450px", borderRadius: "24px", overflow: "hidden", position: "relative", boxShadow: "0 25px 50px rgba(0,0,0,0.3)" }}>
                  <AnimatePresence mode="popLayout">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.2, ease: "easeInOut" }}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url('${visitImages[currentSlide]}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                      }}
                    />
                  </AnimatePresence>
                  <div style={{ position: "absolute", bottom: 0, left: 0, width: "100%", padding: "30px", background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)", color: "white", zIndex: 10 }}>
                    <h4 className="mb-1 text-white fw-bold" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.5)" }}>Discover the World</h4>
                    <p className="mb-0 text-white-50 small">Your next adventure awaits.</p>
                  </div>
                  {/* Prev / Next buttons */}
                  <button
                    onClick={() => setCurrentSlide((prev) => (prev - 1 + visitImages.length) % visitImages.length)}
                    className="slideshow-arrow slideshow-arrow-left"
                    aria-label="Previous slide"
                  >&#8249;</button>
                  <button
                    onClick={() => setCurrentSlide((prev) => (prev + 1) % visitImages.length)}
                    className="slideshow-arrow slideshow-arrow-right"
                    aria-label="Next slide"
                  >&#8250;</button>
                  {/* Dots */}
                  <div style={{ position: "absolute", bottom: "20px", right: "30px", display: "flex", gap: "8px", zIndex: 10 }}>
                    {visitImages.map((_, i) => (
                      <div
                        key={i}
                        onClick={() => setCurrentSlide(i)}
                        style={{
                          width: "8px", height: "8px", borderRadius: "50%",
                          background: i === currentSlide ? "var(--accent-teal, #36948c)" : "rgba(255, 255, 255, 0.4)",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          transform: i === currentSlide ? "scale(1.3)" : "scale(1)"
                        }}
                      />
                    ))}
                  </div>
                </div>
                {/* Mobile Fallback Image */}
                <div className="d-lg-none visit-hero-image mt-4">
                  <img src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=400&fit=crop&q=65" alt="Travel Destinations" className="img-fluid rounded-4 shadow-premium border-glass" loading="lazy" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Visit Types Section */}
      <section className="visit-types section">
        <div className="container">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-5 section-title"
          >
            Visit Visa Services
          </motion.h2>
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
              hidden: {}
            }}
            className="row g-4"
          >
            <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="col-lg-4 col-md-6">
              <div className="visit-card glass-card hover-lift h-100">
                <div className="visit-icon">
                  <FaUserFriends />
                </div>
                <h3>Tourist Visa</h3>
                <p>Explore breathtaking destinations, experience new cultures, and create unforgettable memories with our tourist visa services.</p>
                <ul className="visit-features">
                  <li>Multiple entry options</li>
                  <li>Extended stay periods</li>
                  <li>Fast processing</li>
                  <li>Document assistance</li>
                </ul>
              </div>
            </motion.div>
            <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="col-lg-4 col-md-6">
              <div className="visit-card glass-card hover-lift h-100">
                <div className="visit-icon">
                  <FaPlane />
                </div>
                <h3>Business Visa</h3>
                <p>Expand your business horizons with seamless business visa processing for meetings, conferences, and partnerships.</p>
                <ul className="visit-features">
                  <li>Corporate packages</li>
                  <li>Priority processing</li>
                  <li>Multiple country access</li>
                  <li>Business documentation</li>
                </ul>
              </div>
            </motion.div>
            <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="col-lg-4 col-md-6">
              <div className="visit-card glass-card hover-lift h-100">
                <div className="visit-icon">
                  <FaMapMarkedAlt />
                </div>
                <h3>Family Visit</h3>
                <p>Reunite with loved ones abroad with our specialized family visit visa services and comprehensive support.</p>
                <ul className="visit-features">
                  <li>Family sponsorship</li>
                  <li>Relationship documentation</li>
                  <li>Group applications</li>
                  <li>Extended validity</li>
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="destinations section light-bg">
        <div className="container">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-5 section-title"
          >
            Popular Visit Destinations
          </motion.h2>
          <RevealGroup as="div" className="destinations-grid" stagger={0.07}>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://flagcdn.com/w80/us.png" alt="US" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>United States</h4>
              <p>B1/B2 Tourist & Business Visa</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 15-30 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 10 yrs</span>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://flagcdn.com/w80/ca.png" alt="CA" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>Canada</h4>
              <p>Visitor Visa (TRV)</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 20-40 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 10 yrs</span>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://flagcdn.com/w80/gb.png" alt="UK" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>United Kingdom</h4>
              <p>Standard Visitor Visa</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 15-25 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 10 yrs</span>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://flagcdn.com/w80/au.png" alt="AU" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>Australia</h4>
              <p>Visitor Visa (Subclass 600)</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 20-30 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 10 yrs</span>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://flagcdn.com/w80/nz.png" alt="NZ" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>New Zealand</h4>
              <p>Visitor Visa</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 20-30 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 5 yrs</span>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://upload.wikimedia.org/wikipedia/commons/b/b7/Flag_of_Europe.svg" alt="EU" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>Europe</h4>
              <p>Schengen Visa</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 10-15 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 5 yrs</span>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://flagcdn.com/w80/ie.png" alt="IE" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>Ireland</h4>
              <p>Short Stay Visa</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 15-20 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 5 yrs</span>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="zoom" className="destination-item glass-card hover-lift va-hover-lift">
              <div className="text-center mb-3"><img src="https://flagcdn.com/w80/sg.png" alt="SG" style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'}} /></div>
              <h4>Singapore</h4>
              <p>Tourist/Business Visa</p>
              <div className="destination-info">
                <span><FaClock className="text-primary" /> 3-5 days</span>
                <span><FaCalendarAlt className="text-success" /> Up to 10 yrs</span>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Process Section */}
      <section className="process section">
        <div className="container">
          <Reveal as="h2">Our Simple Process</Reveal>
          <RevealGroup as="div" className="visit-process-timeline" stagger={0.12}>
            <RevealItem as="div" direction="left" className="visit-process-step">
              <div className="visit-step-number">1</div>
              <div className="visit-step-content">
                <h4>Consultation</h4>
                <p>Free assessment of your travel plans and visa requirements</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="left" className="visit-process-step">
              <div className="visit-step-number">2</div>
              <div className="visit-step-content">
                <h4>Documentation</h4>
                <p>Complete document checklist and preparation assistance</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="left" className="visit-process-step">
              <div className="visit-step-number">3</div>
              <div className="visit-step-content">
                <h4>Application</h4>
                <p>Professional application submission and tracking</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="left" className="visit-process-step">
              <div className="visit-step-number">4</div>
              <div className="visit-step-content">
                <h4>Approval</h4>
                <p>Visa collection and pre-travel guidance</p>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose section light-bg">
        <div className="container">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-5 section-title"
          >
            Why Choose Our Visit Visa Services?
          </motion.h2>
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
              hidden: {}
            }}
            className="row g-4"
          >
            <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="col-lg-3 col-md-6">
              <div className="feature-box glass-card hover-lift h-100 p-4 text-center">
                <FaShieldAlt className="visit-feature-icon text-primary mb-3" />
                <h4>Guaranteed Security</h4>
                <p>Your documents and personal information are completely secure with us</p>
              </div>
            </motion.div>
            <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="col-lg-3 col-md-6">
              <div className="feature-box glass-card hover-lift h-100 p-4 text-center">
                <FaClock className="visit-feature-icon text-primary mb-3" />
                <h4>Fast Processing</h4>
                <p>Quick turnaround times with priority processing options available</p>
              </div>
            </motion.div>
            <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="col-lg-3 col-md-6">
              <div className="feature-box glass-card hover-lift h-100 p-4 text-center">
                <FaGlobe className="visit-feature-icon text-primary mb-3" />
                <h4>Global Network</h4>
                <p>Extensive network of partners and contacts worldwide</p>
              </div>
            </motion.div>
            <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="col-lg-3 col-md-6">
              <div className="feature-box glass-card hover-lift h-100 p-4 text-center">
                <FaPassport className="visit-feature-icon text-primary mb-3" />
                <h4>Expert Guidance</h4>
                <p>Professional consultants with years of visa processing experience</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section position-relative overflow-hidden">
        <div className="cta-overlay absolute-bg"></div>
        <div className="container position-relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-content visit-gradient-card text-white p-5 rounded-4 shadow-premium mx-auto" style={{ maxWidth: '800px' }}
          >
            <h2 className="text-white mb-4">Ready to Start Your Journey?</h2>
            <p className="text-white opacity-75 mb-4">Get expert assistance for your visit visa application today. Our team is here to make your travel dreams come true.</p>
            <div className="cta-buttons d-flex justify-content-center flex-wrap gap-3 mt-4">
              <MagneticButton as="span">
                <Link to="/consultationForm" className="premium-btn va-shimmer-btn px-4 text-white" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '54px' }}>
                  Get Free Consultation
                </Link>
              </MagneticButton>
              <Link to="/" className="btn btn-outline-light px-4" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '54px', borderRadius: '12px', border: '2px solid white' }}>
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}